import api from '@/lib/api';

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  last_four: string;
  organization_id: string;
  description?: string;
  permissions: {
    read: boolean;
    write: boolean;
  };
  is_active: boolean;
  expires_at?: string;
  last_used_at?: string;
  ip_whitelist?: string[];
  rate_limit?: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
  api_key?: string; // Only present at creation
}

export interface CreateApiKeyData {
  name: string;
  description?: string;
  environment?: 'live' | 'test';
  permissions?: {
    read: boolean;
    write: boolean;
  };
  expiresAt?: string | null;
  organizationId?: string;
}

export interface UpdateApiKeyData {
  name?: string;
  description?: string;
  permissions?: {
    read: boolean;
    write: boolean;
  };
  isActive?: boolean;
  expiresAt?: string | null;
  ipWhitelist?: string[];
  rateLimit?: number;
  organizationId?: string;
}

export interface ApiKeyUsageStats {
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  avg_duration_ms: number;
  date: string;
}

class ApiKeysService {
  /**
   * Generate a new API key
   */
  async generateApiKey(data: CreateApiKeyData) {
    const response = await api.post('/api-keys', data);
    return response.data;
  }

  /**
   * List all API keys for organization
   */
  async listApiKeys(organizationId?: string) {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get('/api-keys', { params });
    return response.data;
  }

  /**
   * Get API key details
   */
  async getApiKey(id: string, organizationId?: string) {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get(`/api-keys/${id}`, { params });
    return response.data;
  }

  /**
   * Update API key
   */
  async updateApiKey(id: string, data: UpdateApiKeyData) {
    const response = await api.put(`/api-keys/${id}`, data);
    return response.data;
  }

  /**
   * Delete API key
   */
  async deleteApiKey(id: string, organizationId?: string) {
    const params = organizationId ? { organizationId } : {};
    const response = await api.delete(`/api-keys/${id}`, { params });
    return response.data;
  }

  /**
   * Get API key usage statistics
   */
  async getUsageStats(id: string, startDate?: string, endDate?: string) {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await api.get(`/api-keys/${id}/usage`, { params });
    return response.data;
  }
}

export default new ApiKeysService();
